<template>
  <section>
    <r-title>{{ $t("title") | titlecase }}</r-title>

    <ProcessStepBox
      :step="1"
      :active-step="step"
      :loading="!customers"
      :selectable="customerSelectable"
      :title="$t('customers.title') | titlecase"
      @click="toStep(1)"
    >
      <p v-if="!customers">{{ $t("customers.is-loading") }}</p>
      <template v-else-if="customers.length">
        <p>{{ $t("customers.prompt") }}</p>
        <CustomerSelect v-model="customer" :customers="customers" />
      </template>
      <p v-else>{{ $t("customers.is-empty") }}</p>
      <div class="buttons">
        <r-button :disabled="!customerReady" prominent @click.stop="toStep(2)">
          {{ $t("customers.button") }}
        </r-button>
      </div>
    </ProcessStepBox>

    <ProcessStepBox
      :step="2"
      :active-step="step"
      :selectable="filesSelectable"
      :title="$t('upload.title') | titlecase"
      @click="toStep(2)"
    >
      <p v-if="customer">
        {{ $t("customers.prompt", { customer: customer.name }) }}
      </p>
      <div class="field">
        <FilesInput v-model="file1" :disabled="uploadState != null">
          {{ $t("upload.select-doc1") | capitalize }}
        </FilesInput>
      </div>
      <div class="field">
        <FilesInput v-model="file2" :disabled="uploadState != null">
          {{ $t("upload.select-doc2") | capitalize }}
        </FilesInput>
      </div>
      <div class="field">
        <FilesInput v-model="file3" :disabled="uploadState != null">
          {{ $t("upload.select-doc3") | capitalize }}
        </FilesInput>
      </div>
      <p v-if="uploadState === 'active'">{{ $t("upload.is-submitting") }}</p>
      <p v-else-if="uploadState === 'success'">{{ $t("upload.success") }}</p>
      <p v-else-if="uploadState === 'failure'">{{ $t("upload.failure") }}</p>
      <div class="buttons">
        <r-button
          :disabled="!filesUploadable"
          :loading="uploadState === 'active'"
          prominent
          @click="startUpload"
        >
          {{
            uploadState === "failure"
              ? $t("upload.button.retry")
              : $t("upload.button.upload")
          }}
        </r-button>
      </div>
    </ProcessStepBox>

    <ProcessStepBox
      :step="3"
      :active-step="step"
      :selectable="filesUploaded"
      :title="$t('submit.title') | titlecase"
    >
      <p v-if="!filesUploaded">{{ $t("submit.prompt") }}</p>
      <div v-else-if="!submitState" class="content">
        <p>{{ $t("submit.prompt", { customer: customer.name }) }}</p>
        <ul>
          <li>{{ file1[0].name }}</li>
          <li>{{ file2[0].name }}</li>
          <li v-if="file3 && file3.length && file3[0]">{{ file3[0].name }}</li>
        </ul>
      </div>
      <p v-if="submitState === 'active'">{{ $t("submit.is-submitting") }}</p>
      <p v-else-if="submitState === 'success'">{{ $t("submit.success") }}</p>
      <p v-else-if="submitState === 'failure'">{{ $t("submit.failure") }}</p>
      <p v-if="abortState === 'active'">{{ $t("delete.is-submitting") }}</p>
      <p v-else-if="abortState === 'success'">{{ $t("delete.success") }}</p>
      <p v-else-if="abortState === 'failure'">{{ $t("delete.failure") }}</p>
      <div class="buttons">
        <r-button
          v-if="filesUploaded && !taskFinished"
          :loading="submitState === 'active'"
          :disabled="submitState === 'active' || abortState === 'active'"
          prominent
          @click="submitTask"
        >
          {{
            submitState === "failure"
              ? $t("submit.button.retry")
              : $t("submit.button.submit")
          }}
        </r-button>
        <r-button
          v-if="filesUploaded && !taskFinished"
          :loading="abortState === 'active'"
          :disabled="submitState === 'active' || abortState === 'active'"
          class="is-danger"
          @click="abortTask"
        >
          {{ $t("delete.button") }}
        </r-button>
        <r-button v-if="taskFinished" @click="resetPage">
          {{ $t("submit.button.reset") }}
        </r-button>
      </div>
    </ProcessStepBox>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { vueLogger } from "@/compose";
import { ProcessStepBox } from "@apphq/vue-base-components";
import { FilesInput } from "@apphq/vue-form";

import CustomerSelect from "@/components/CustomerSelect.vue";

export default {
  name: "TaskCreatePage",
  components: {
    CustomerSelect,
    ProcessStepBox,
    FilesInput
  },
  i18nOptions: {
    keyPrefix: "views.task-create"
  },
  props: {
    unsubmittedTaskId: {
      type: Number,
      default: null
    }
  },
  data() {
    const data = {
      customer: null,
      file1: null,
      file2: null,
      file3: null,
      uploadState: null,
      taskId: null,
      submitState: null,
      abortState: null,
      step: 1
    };

    if (this.unsubmittedTaskId != null) {
      const task =
        this.$store.getters.unsubmittedTasks &&
        this.$store.getters.unsubmittedTasks.find(
          task => task.taskId === this.unsubmittedTaskId
        );
      if (task) {
        data.step = 3;
        data.uploadState = "success";
        data.taskId = task.taskId;
        data.customer = {
          name: task.customerName
        };
        data.file1 = [{ name: task.file1Name }];
        data.file2 = [{ name: task.file2Name }];
        if (task.file3Name) {
          data.file3 = [{ name: task.file3Name }];
        }
      }
    }
    return data;
  },
  computed: {
    customerSelectable() {
      return !this.uploadState;
    },
    customerReady() {
      return Boolean(this.customer);
    },
    filesSelectable() {
      return (
        this.customerReady &&
        (!this.uploadState || this.uploadState === "failed")
      );
    },
    filesUploadable() {
      return (
        this.customer &&
        this.file1 &&
        this.file2 &&
        this.uploadState !== "active" &&
        this.uploadState !== "success"
      );
    },
    filesUploaded() {
      return this.uploadState === "success";
    },
    filesSubmitable() {
      return (
        this.filesUploaded &&
        (!this.submitState || this.submitState === "failed") &&
        !this.abortState
      );
    },
    taskFinished() {
      return this.submitState === "success" || this.abortState === "success";
    },
    ...mapGetters(["customers"])
  },
  beforeCreate() {
    this.$store.dispatch("loadCustomers");
  },
  methods: {
    toStep(step) {
      if (
        (step === 1 && !this.customerSelectable) ||
        (step === 2 && !this.filesSelectable) ||
        (step === 3 && !this.filesUploaded)
      ) {
        return;
      }
      this.step = step;
    },
    startUpload() {
      this.uploadState = "active";
      this.$store
        .dispatch("createTask", {
          customer: this.customer.name,
          file1: this.file1[0],
          file2: this.file2[0],
          file3: this.file3 && this.file3[0],
          deleteAfter: 84
        })
        .then(taskId => {
          this.taskId = taskId;
          this.uploadState = "success";
          this.toStep(3);
        })
        .catch(error => {
          vueLogger.push("ERROR", JSON.stringify(error));
          vueLogger.push("ERROR", error.message);
          this.uploadState = "failure";
        });
    },
    submitTask() {
      this.submitState = "active";
      this.$store
        .dispatch("submitTask", this.taskId)
        .then(() => {
          this.submitState = "success";
        })
        .catch(error => {
          vueLogger.push("ERROR", JSON.stringify(error));
          vueLogger.push("ERROR", error.message);
          this.submitState = "failure";
        });
    },
    abortTask() {
      this.abortState = "active";
      this.$store
        .dispatch("abortTaskCreation", this.taskId)
        .then(() => {
          this.abortState = "success";
        })
        .catch(error => {
          vueLogger.push("ERROR", JSON.stringify(error));
          vueLogger.push("ERROR", error.message);
          this.abortState = "failure";
        });
    },
    resetPage() {
      this.uploadState = this.file1 = this.file2 = this.file3 = null;
      this.taskId = this.submitState = this.abortState = null;
      this.step = 1;
      window.scroll({
        top: 0,
        behaviour: "smooth"
      });
    }
  }
};
</script>

<style lang="scss">
@import "~@apphq/rhenus-design-base/sass/variables.scss";
@import "~bulma/sass/elements/content.sass";
</style>
